import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { documentService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { type DocumentFormData } from '@/lib/validations/document'
import { toast } from 'sonner'
import { useWorkspaceData } from '@/hooks/useWorkspaceData'
import { useDocumentAssociations } from '@/hooks/useDocumentAssociations'
import { PageContainer, PageHeader } from '@/components/layout'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'
import { DocumentForm } from '@/components/documents/DocumentForm'

export default function DocumentCreatePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('')
  const [isDirty, setIsDirty] = useState(false)

  // Load workspace data
  const { workspaces, projects, tasks, users } = useWorkspaceData({
    userId: user?.id,
    workspaceId: selectedWorkspaceId,
  })

  // Manage associations
  const {
    selectedProjects,
    selectedTasks,
    selectedUsers,
    handleAddProject,
    handleRemoveProject,
    handleAddTask,
    handleRemoveTask,
    handleAddUser,
    handleRemoveUser,
    filterAssociationsByAvailable,
  } = useDocumentAssociations()

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty,
  })

  // Clear associations that don't belong to the new workspace when workspace changes
  useEffect(() => {
    if (selectedWorkspaceId) {
      filterAssociationsByAvailable(projects, tasks, users)
    } else {
      // If no workspace is selected, clear all associations
      filterAssociationsByAvailable([], [], [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspaceId, projects, tasks, users])

  const onSubmit = async (data: DocumentFormData) => {
    if (!user) return

    if (!uploadedFile && !data.url) {
      toast.error('Please upload a file or provide a URL')
      return
    }

    setLoading(true)
    try {
      // Create document data with file and associations
      const documentData: DocumentFormData = {
        ...data,
        file: uploadedFile || undefined,
        projectIds: selectedProjects.map((p) => p.id),
        taskIds: selectedTasks.map((t) => t.id),
        userIds: selectedUsers.map((u) => u.id),
      }

      const newDocument = await documentService.createDocument(documentData, user.id)

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      toast.success('Document created successfully')
      navigate(`/documents/${newDocument.id}`)
    } catch (error) {
      console.error('Failed to create document:', error)
      toast.error('Failed to create document')
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader title="Upload New Document" />

      <DocumentForm
        mode="create"
        workspaces={workspaces}
        projects={projects}
        tasks={tasks}
        users={users}
        selectedProjects={selectedProjects}
        selectedTasks={selectedTasks}
        selectedUsers={selectedUsers}
        onAddProject={(id) => handleAddProject(id, projects)}
        onRemoveProject={handleRemoveProject}
        onAddTask={(id) => handleAddTask(id, tasks)}
        onRemoveTask={handleRemoveTask}
        onAddUser={(id) => handleAddUser(id, users)}
        onRemoveUser={handleRemoveUser}
        onWorkspaceChange={setSelectedWorkspaceId}
        uploadedFile={uploadedFile}
        onFileChange={setUploadedFile}
        onSubmit={onSubmit}
        onCancel={() => navigate('/documents')}
        submitText={loading ? 'Uploading...' : 'Upload Document'}
        loading={loading}
        title="Document Details"
        description="Upload a document or provide a link to external file"
        onDirtyChange={setIsDirty}
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
